import type { Persona, StudioId } from '@/domain/types';
import { STUDIO_IDS } from '@/data/studios';

// Derived from the studio map so the network-wide persona coverage can never
// drift from the real 11 studios.
export const ALL_STUDIOS: StudioId[] = STUDIO_IDS;

export interface PersonaCardSpec {
  persona: Persona;
  name: string;
  role: string;
  description: string;
  defaultHome: string;
  destination: string;
  agentId?: string;
  artistId?: string;
  apprenticeId?: string;
  assignedStudios?: StudioId[];
  activeStudio?: StudioId;
}

export const PERSONA_CARDS: PersonaCardSpec[] = [
  {
    persona: 'georgina',
    name: 'Georgina Gray',
    role: 'Operations Lead',
    description: 'Network-wide view. Eleven studios. Approvals, escalations, AI hand-offs.',
    defaultHome: 'Network View',
    destination: '/network',
    agentId: 'georgina',
    assignedStudios: ALL_STUDIOS,
    activeStudio: 'coburg',
  },
  {
    persona: 'studio-manager',
    name: 'Eli Marchetti',
    role: 'Studio Manager. Coburg.',
    description: 'Run the day at one studio. Calendar, deposits, missed calls, front desk.',
    defaultHome: 'Studio View. Coburg',
    destination: '/studio/coburg',
    agentId: 'mgr-coburg',
    assignedStudios: ['coburg'],
    activeStudio: 'coburg',
  },
  {
    persona: 'bookings-agent',
    name: 'Cameron Wells',
    role: 'Bookings Agent. Coburg + Collingwood + Frankston.',
    description: 'My queue, one-click dial, deposit links mid-conversation.',
    defaultHome: 'My Queue',
    destination: '/queue',
    agentId: 'agent-cameron',
    assignedStudios: ['coburg', 'collingwood', 'frankston'],
    activeStudio: 'coburg',
  },
  {
    persona: 'artist',
    name: 'Pat Donnelly',
    role: 'Artist. Coburg.',
    description: "Today's sessions, design briefs, in-chair rebooking, earnings.",
    defaultHome: 'Artist Dashboard',
    destination: '/artist/pat',
    artistId: 'pat',
    assignedStudios: ['coburg'],
    activeStudio: 'coburg',
  },
  {
    persona: 'apprentice',
    name: 'Tia Bennett',
    role: 'Apprentice. Coburg.',
    description: 'Own sessions, front desk capture, restricted scope.',
    defaultHome: 'Apprentice Dashboard',
    destination: '/apprentice/apprentice-tia',
    apprenticeId: 'apprentice-tia',
    assignedStudios: ['coburg'],
    activeStudio: 'coburg',
  },
  {
    persona: 'owner',
    name: 'Jake Boyer',
    role: 'Co-owner.',
    description: 'Strategic dashboard, per-studio P&L, AI vs human cost.',
    defaultHome: 'Owner Dashboard',
    destination: '/owner',
    agentId: 'owner',
    assignedStudios: ALL_STUDIOS,
    activeStudio: 'coburg',
  },
  // The two back-office finance seats (diagram 11). Both land on the money-loop
  // (reconciliation) screen. The route/nav gating that lets these seats reach it
  // and hides the non-finance surfaces is wired separately by tv-1xc.
  {
    persona: 'finance',
    name: 'Robin Ashby',
    role: 'Finance / Accounts. Network.',
    description: "Reconcile the day's takings, match settlement, resolve unmatched money.",
    defaultHome: 'Reconciliation',
    destination: '/reconciliation',
    assignedStudios: ALL_STUDIOS,
    activeStudio: 'coburg',
  },
  {
    persona: 'finance-manager',
    name: 'Dana Whitlock',
    role: 'Finance Manager. Network.',
    description: 'Sign off what Accounts resolved. Maker-checker on unmatched money.',
    defaultHome: 'Reconciliation',
    destination: '/reconciliation',
    assignedStudios: ALL_STUDIOS,
    activeStudio: 'coburg',
  },
];

export function findPersonaCard(persona: Persona): PersonaCardSpec | undefined {
  return PERSONA_CARDS.find((c) => c.persona === persona);
}
